import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { FormField } from '@/components/Form/FormField';
import { FormEvent, useEffect } from 'react';
import { Controller, FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { RichTextEditor } from '@/components/Custom/RichTextEditor';
import { allFieldErrorsEmpty } from '@/utils/object.utils';
import { containsOnlyEmptyHTMLTags } from '@/utils/string.utils';
import { getTranslatedItem } from '@/utils/language.utils';
import { setGroupRequiredFields } from '@/utils/required-fields/group.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';

export const GroupWhiteLabelInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const translatedGroup = getTranslatedItem(data?.translatedGroups);

  /**
   * === Set Value Watchers section ===
   */
  const fromNameValue =
    watch && watch('fromName' as Path<TSchema>, data?.fromName ?? '');

  const fromEmailValue =
    watch && watch('fromEmail' as Path<TSchema>, data?.fromEmail ?? '');

  const replyToEmailValue =
    watch && watch('replyToEmail' as Path<TSchema>, data?.replyToEmail ?? '');

  const individualEmailSubjectValue =
    watch &&
    watch(
      'individualEmailSubject' as Path<TSchema>,
      data?.individualEmailSubject ?? ''
    );

  const individualEmailContentValue =
    watch &&
    watch(
      'individualEmailContent' as Path<TSchema>,
      data?.individualEmailContent ?? ''
    );
  /**
   * === END OF Set Value Watchers section ===
   */

  /**
   * === Reset Error Messages On First Render section (when on tab change) ===
   */
  useEffect(() => {
    setError('fromName' as Path<TSchema>, {
      message: '',
    });
    setError('fromEmail' as Path<TSchema>, {
      message: '',
    });
    setError('replyToEmail' as Path<TSchema>, {
      message: '',
    });
    setError('individualEmailSubject' as Path<TSchema>, {
      message: '',
    });
    setError('individualEmailContent' as Path<TSchema>, {
      message: '',
    });
  }, [setError]);
  /**
   * === END OF Reset Error Messages On First Render section (when on tab change) ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setGroupRequiredFields({ setValue, data });
      setValue('fromName' as Path<TSchema>, data.fromName ?? '');
      setValue('fromEmail' as Path<TSchema>, data.fromEmail ?? '');
      setValue('replyToEmail' as Path<TSchema>, data.replyToEmail ?? '');
      setValue(
        'individualEmailSubject' as Path<TSchema>,
        (translatedGroup?.individualEmailSubject ||
          data.individualEmailSubject) ??
          ''
      );
      setValue(
        'individualEmailContent' as Path<TSchema>,
        (translatedGroup?.individualEmailContent ||
          data.individualEmailContent) ??
          ''
      );
      setValue(
        'legalNotice' as Path<TSchema>,
        (translatedGroup?.legalNotice || data.legalNotice) ?? ''
      );
      setValue(
        'privacyPolicy' as Path<TSchema>,
        (translatedGroup?.privacyPolicy || data.privacyPolicy) ?? ''
      );
      setValue(
        'cgu' as Path<TSchema>,
        (translatedGroup?.cgu || data.cgu) ?? ''
      );
      setValue(
        'appWarningMessage' as Path<TSchema>,
        (translatedGroup?.appWarningMessage || data.appWarningMessage) ?? ''
      );
      setValue(
        'partFolderNotFound' as Path<TSchema>,
        (translatedGroup?.partFolderNotFound || data.partFolderNotFound) ?? ''
      );
      setValue(
        'partContactUs' as Path<TSchema>,
        (translatedGroup?.partContactUs || data.partContactUs) ?? ''
      );
      setValue(
        'proContactUs' as Path<TSchema>,
        (translatedGroup?.proContactUs || data.proContactUs) ?? ''
      );
    }
  }, [setValue, data, translatedGroup]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (fromNameValue) {
      setError('fromName' as Path<TSchema>, {
        message: '',
      });
    }
  }, [fromNameValue, setError]);

  useEffect(() => {
    if (fromEmailValue) {
      setError('fromEmail' as Path<TSchema>, {
        message: '',
      });
    }
  }, [fromEmailValue, setError]);

  useEffect(() => {
    if (replyToEmailValue) {
      setError('replyToEmail' as Path<TSchema>, {
        message: '',
      });
    }
  }, [replyToEmailValue, setError]);

  useEffect(() => {
    if (individualEmailSubjectValue) {
      setError('individualEmailSubject' as Path<TSchema>, {
        message: '',
      });
    }
  }, [individualEmailSubjectValue, setError]);

  useEffect(() => {
    if (individualEmailContentValue) {
      setError('individualEmailContent' as Path<TSchema>, {
        message: '',
      });
    }
  }, [individualEmailContentValue, setError]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!fromNameValue) {
      setError('fromName' as Path<TSchema>, {
        message: t('common:senderNameRequired'),
      });
    }

    if (!fromEmailValue) {
      setError('fromEmail' as Path<TSchema>, {
        message: t('common:senderEmailRequired'),
      });
    }

    if (!replyToEmailValue) {
      setError('replyToEmail' as Path<TSchema>, {
        message: t('common:replyToEmailRequired'),
      });
    }

    if (!individualEmailSubjectValue) {
      setError('individualEmailSubject' as Path<TSchema>, {
        message: t('common:emailSubjectRequired'),
      });
    }

    if (
      !individualEmailContentValue ||
      containsOnlyEmptyHTMLTags(individualEmailContentValue)
    ) {
      setError('individualEmailContent' as Path<TSchema>, {
        message: t('common:emailContentRequired'),
      });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  const getCardTitle = (title: string) => {
    return (
      <TitleSection text={title} defaultIconClassName="ki-notepad-bookmark" />
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={getCardTitle(t('common:defaultEmail'))}
        cardClassName="mb-5"
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField
            text={t('common:senderName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('fromName' as Path<TSchema>))}
                errorMessage={errors && (errors.fromName?.message as string)}
                placeholder={t('common:senderName')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:senderEmail')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('fromEmail' as Path<TSchema>))}
                errorMessage={errors && (errors.fromEmail?.message as string)}
                placeholder={t('common:senderEmail')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:replyTo')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('replyToEmail' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.replyToEmail?.message as string)
                }
                placeholder={t('common:replyTo')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:emailSubject')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualEmailSubject' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualEmailSubject?.message as string)
                }
                placeholder={t('common:emailSubject')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:emailContent')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Controller
                name="individualEmailContent"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    errorMessage={
                      errors &&
                      (errors.individualEmailContent?.message as string)
                    }
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </FormField>
        </>
      </Card>
      <Card
        title={getCardTitle(t('common:legalInformations'))}
        cardClassName="mb-5"
      >
        <FormField
          containerClassName="mb-5"
          text={t('common:legalMentions')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="legalNotice"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
        <FormField
          containerClassName="mb-5"
          textWrapperClassName="mt-3"
          text={t('common:privacyPolicy')}
        >
          <Controller
            name="privacyPolicy"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
        <FormField text={t('common:cgu')} textWrapperClassName="mt-3">
          <Controller
            name="cgu"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
      </Card>
      <Card
        cardClassName="mb-5"
        title={getCardTitle(t('common:infoLinkedForPart'))}
      >
        <FormField
          containerClassName="mb-5"
          text={t('common:referenceNumberNotFound')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="partFolderNotFound"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
        <FormField
          containerClassName="mb-5"
          text={t('common:contactUs')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="partContactUs"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
      </Card>
      <Card
        cardClassName="mb-5"
        title={getCardTitle(t('common:infoLinkedForPro'))}
      >
        <FormField
          containerClassName="mb-5"
          text={t('common:contactUs')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="proContactUs"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
      </Card>
      <Card
        title={getCardTitle(t('common:app'))}
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
      >
        <FormField
          containerClassName="mb-5"
          text={t('common:appWarningMessage')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="appWarningMessage"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
      </Card>
    </form>
  );
};
